import { Suspense } from "react"
import { lazyRetry } from "../lib/lazy-retry"
import { Routes, Route, Navigate } from "react-router-dom"
import { DashboardLayout } from "../components/layout/DashboardLayout"
import { EditorLayout } from "../components/layout/EditorLayout"
import { TestLayout } from "../components/layout/TestLayout"
import { RequireAuth } from "../components/system/auth"

// Lazy load pages for better performance
const Overview = lazyRetry(() => import("../features/overview/Overview"))

// Test pages
const DaveKnowledgePage = lazyRetry(() => import("../features/test-pages/pages/dave-knowledge/DaveKnowledgePage"))

// Contract Template Pages
const ContractTemplateEditor = lazyRetry(() => import("../features/contract-templates/pages/ContractTemplateEditor"))
const ContractSendEditor = lazyRetry(() => import("../features/contract-templates/pages/ContractSendEditor"))

// Signing Feature Pages
const SigningEditor = lazyRetry(() => import("../features/signing/pages/SigningEditor"))
const SigningAccess = lazyRetry(() => import("../features/signing/pages/SigningAccess"))
const SigningTemplatesList = lazyRetry(() => import("../features/signing/pages/SigningTemplatesList"))
const SigningDocumentsList = lazyRetry(() => import("../features/signing/pages/SigningDocumentsList"))
const SigningDocumentView = lazyRetry(() => import("../features/signing/pages/SigningDocumentView"))
const Matters = lazyRetry(() => import("../features/matters/pages/MattersList"))
const MatterDetail = lazyRetry(() => import("../features/matters/pages/MatterDetail"))
const Contacts = lazyRetry(() => import("../features/contacts/pages/ContactsList"))
const ContactDetail = lazyRetry(() => import("../features/contacts/pages/ContactDetail"))
const Pipeline = lazyRetry(() => import("../features/pipeline/pages/Pipeline"))
const Tasks = lazyRetry(() => import("../features/tasks/pages/TasksList"))
const ClaimablesList = lazyRetry(() => import("../features/claimables/pages/ClaimablesList"))
const LeadsList = lazyRetry(() => import("../features/leads/pages/LeadsList"))
const LeadsLive = lazyRetry(() => import("../features/leads/pages/LeadsLive"))
const LeadDetail = lazyRetry(() => import("../features/leads/pages/LeadDetail"))
const ApprovalsList = lazyRetry(() => import("../features/approvals/pages/ApprovalsList"))
const ItemsList = lazyRetry(() => import("../features/items/pages/ItemsList"))
const RequestsList = lazyRetry(() => import("../features/requests/pages/RequestsList"))
const MyTicketsList = lazyRetry(() => import("../features/system-tickets/pages/MyTicketsList"))
const CallsList = lazyRetry(() => import("../features/calls/pages/CallsList"))
const CallDetail = lazyRetry(() => import("../features/calls/pages/CallDetail").then(m => ({ default: m.CallDetail })))
const QueueMetrics = lazyRetry(() => import("../features/queue-metrics/pages/QueueMetricsPage"))
const DocumentsList = lazyRetry(() => import("../features/documents/pages/DocumentsList"))
const TextsPage = lazyRetry(() => import("../features/texts/pages/TextsPage"))
const VoicemailList = lazyRetry(() => import("../features/voicemail/pages/VoicemailList"))
const VoicemailDetail = lazyRetry(() => import("../features/voicemail/pages/VoicemailDetail").then(m => ({ default: m.VoicemailDetail })))
const MentionsList = lazyRetry(() => import("../features/mentions/pages/MentionsList"))
const GeneralItemsList = lazyRetry(() => import("../features/general-items/pages/GeneralItemsList"))
const EventsList = lazyRetry(() => import("../features/events/pages/EventsList"))
const SettingsPage = lazyRetry(() => import("../features/settings/pages/SettingsPage"))
const CustomViewsList = lazyRetry(() => import("../features/custom-views/pages/CustomViewsList"))
const CustomViewViewer = lazyRetry(() => import("../features/custom-views/pages/CustomViewViewer"))
const NotFound = lazyRetry(() => import("../features/errors/NotFound"))
const DirectoryPage = lazyRetry(() => import("../features/directory/pages/DirectoryPage"))
const TrainingDashboard = lazyRetry(() => import("../features/training/pages/TrainingDashboard"))
const CourseViewer = lazyRetry(() => import("../features/training/pages/CourseViewer"))
const StandaloneQuiz = lazyRetry(() => import("../features/training/pages/StandaloneQuizPage"))
const SimulationPlayer = lazyRetry(() => import("../features/training/pages/SimulationPlayer"))
const ScenariosListPage = lazyRetry(() => import("../features/training/pages/ScenariosListPage"))
const SimulationReviewPage = lazyRetry(() => import("../features/training/pages/SimulationReviewPage"))
const CalendarPage = lazyRetry(() => import("../features/calendar/pages/CalendarPage"))
const TimeEntriesPage = lazyRetry(() => import("../features/time-tracking/pages/TimeEntriesPage"))
const CollectionsList = lazyRetry(() => import("../features/collections/pages/CollectionsList"))
const NotificationsPage = lazyRetry(() => import("../features/notifications/pages/NotificationsPage"))
const ChangelogPage = lazyRetry(() => import("../features/changelog/pages/ChangelogPage"))

// BK Modules - Standalone Pages
const NewCaseEntriesPage = lazyRetry(() => import("../features/bankruptcy/modules/pages/NewCaseEntriesPage"))
const DismissedCasesPage = lazyRetry(() => import("../features/bankruptcy/modules/pages/DismissedCasesPage"))
const ConvertedCasesPage = lazyRetry(() => import("../features/bankruptcy/modules/pages/ConvertedCasesPage"))
const BkItemsPage = lazyRetry(() => import("../features/bankruptcy/modules/pages/BkItemsPage"))
const TrusteeDocumentsPage = lazyRetry(() => import("../features/bankruptcy/modules/pages/TrusteeDocumentsPage"))
const BkHearingsPage = lazyRetry(() => import("../features/bankruptcy/modules/pages/BkHearingsPage"))

// Matter Detail - Tab Components (lazy loaded)
const OverviewTab = lazyRetry(() => import("../features/matters/pages/tabs/OverviewTab"))
const FeedTab = lazyRetry(() => import("../features/matters/pages/tabs/FeedTab"))
const EventsTab = lazyRetry(() => import("../features/matters/pages/tabs/EventsTab"))
const BillingTab = lazyRetry(() => import("../features/matters/pages/tabs/BillingTab"))
const BillingPaymentsTab = lazyRetry(() => import("../features/matters/pages/tabs/BillingTab/tabs/PaymentsTab"))
const BillingMethodsTab = lazyRetry(() => import("../features/matters/pages/tabs/BillingTab/tabs/MethodsTab"))
const BillingChargeTab = lazyRetry(() => import("../features/matters/pages/tabs/BillingTab/tabs/ChargeTab"))
const BillingRecurringTab = lazyRetry(() => import("../features/matters/pages/tabs/BillingTab/tabs/RecurringTab"))
const BillingInvoicesTab = lazyRetry(() => import("../features/matters/pages/tabs/BillingTab/tabs/InvoicesTab"))
const BillingTrustTab = lazyRetry(() => import("../features/matters/pages/tabs/BillingTab/tabs/TrustTab"))
const BillingScheduleTab = lazyRetry(() => import("../features/matters/pages/tabs/BillingTab/tabs/ScheduleTab"))
const BillingEventsTab = lazyRetry(() => import("../features/matters/pages/tabs/BillingTab/tabs/EventsTab"))
const BillingTimeTab = lazyRetry(() => import("../features/matters/pages/tabs/BillingTab/tabs/TimeTab"))
const SettingsTab = lazyRetry(() => import("../features/matters/pages/tabs/SettingsTab"))
const CustomFieldsTab = lazyRetry(() => import("../features/matters/pages/tabs/CustomFieldsTab"))
const CustomDatesTab = lazyRetry(() => import("../features/matters/pages/tabs/CustomDatesTab"))
const AutomationsTab = lazyRetry(() => import("../features/matters/pages/tabs/AutomationsTab"))
const ApprovalsTab = lazyRetry(() => import("../features/matters/pages/tabs/ApprovalsTab"))
const RequestsTab = lazyRetry(() => import("../features/matters/pages/tabs/RequestsTab"))
const IdentifiersTab = lazyRetry(() => import("../features/matters/pages/tabs/IdentifiersTab"))
const LeadSourcesTab = lazyRetry(() => import("../features/matters/pages/tabs/LeadSourcesTab"))
const CommunicationAnalyticsTab = lazyRetry(() => import("../features/matters/pages/tabs/CommunicationAnalyticsTab"))
const PartiesRolesTab = lazyRetry(() => import("../features/matters/pages/tabs/PartiesRolesTab"))
const ClaimablesTab = lazyRetry(() => import("../features/matters/pages/tabs/ClaimablesTab"))
const NotificationsTab = lazyRetry(() => import("../features/matters/pages/tabs/NotificationsTab"))
const IntakeTab = lazyRetry(() => import("../features/matters/pages/tabs/IntakeTab"))

// Documents Tab and sub-tabs
const DocumentsTab = lazyRetry(() => import("../features/matters/pages/tabs/DocumentsTab"))
const DocumentsCategoriesView = lazyRetry(() => import("../features/matters/pages/tabs/DocumentsTab/CategoriesView"))
const DocumentsListView = lazyRetry(() => import("../features/matters/pages/tabs/DocumentsTab/ListView"))
const DocumentsPinnedView = lazyRetry(() => import("../features/matters/pages/tabs/DocumentsTab/PinnedView"))
const DocumentsSigningView = lazyRetry(() => import("../features/matters/pages/tabs/DocumentsTab/SigningView"))
const DocumentsTemplatesView = lazyRetry(() => import("../features/matters/pages/tabs/DocumentsTab/TemplatesView"))

// Items Tab and sub-tabs
const ItemsTab = lazyRetry(() => import("../features/matters/pages/tabs/ItemsTab"))
const ItemsOutstanding = lazyRetry(() => import("../features/matters/pages/tabs/ItemsTab/OutstandingItems"))
const ItemsCompleted = lazyRetry(() => import("../features/matters/pages/tabs/ItemsTab/CompletedItems"))

// Tasks Tab and sub-tabs (in secondary nav)
const TasksTab = lazyRetry(() => import("../features/matters/pages/tabs/TasksTab"))
const TasksStepTasks = lazyRetry(() => import("../features/matters/pages/tabs/TasksTab/StepTasks"))
const TasksMyTasks = lazyRetry(() => import("../features/matters/pages/tabs/TasksTab/MyTasks"))
const TasksAllTasks = lazyRetry(() => import("../features/matters/pages/tabs/TasksTab/AllTasks"))
const TasksFinishedTasks = lazyRetry(() => import("../features/matters/pages/tabs/TasksTab/FinishedTasks"))

// Step Tab and sub-tabs
const StepTab = lazyRetry(() => import("../features/matters/pages/tabs/StepTab"))
const StepMoveStep = lazyRetry(() => import("../features/matters/pages/tabs/StepTab/MoveStep"))
const StepJumpStep = lazyRetry(() => import("../features/matters/pages/tabs/StepTab/JumpStep"))
const StepHistory = lazyRetry(() => import("../features/matters/pages/tabs/StepTab/History"))

// Forms Tab and sub-tabs
const FormsTab = lazyRetry(() => import("../features/matters/pages/tabs/FormsTab"))
const FormsStepForms = lazyRetry(() => import("../features/matters/pages/tabs/FormsTab/StepForms"))
const FormsAllForms = lazyRetry(() => import("../features/matters/pages/tabs/FormsTab/AllForms"))
const FormsSubmissions = lazyRetry(() => import("../features/matters/pages/tabs/FormsTab/Submissions"))

// Portal Tab and sub-tabs
const PortalTab = lazyRetry(() => import("../features/matters/pages/tabs/PortalTab"))
const PortalOverview = lazyRetry(() => import("../features/matters/pages/tabs/PortalTab/PortalOverview"))
const PortalQuestionnaire = lazyRetry(() => import("../features/matters/pages/tabs/PortalTab/Questionnaire"))
const PortalOnboarding = lazyRetry(() => import("../features/matters/pages/tabs/PortalTab/Onboarding"))
const PortalCCC = lazyRetry(() => import("../features/matters/pages/tabs/PortalTab/CCC"))
const PortalCreditors = lazyRetry(() => import("../features/matters/pages/tabs/PortalTab/Creditors"))
const PortalImport = lazyRetry(() => import("../features/matters/pages/tabs/PortalTab/Import"))
const PortalDocs = lazyRetry(() => import("../features/matters/pages/tabs/PortalTab/Docs"))
const PortalItems = lazyRetry(() => import("../features/matters/pages/tabs/PortalTab/Items"))
const PortalDocsLibrary = lazyRetry(() => import("../features/matters/pages/tabs/PortalTab/DocsLibrary"))
const PortalSettings = lazyRetry(() => import("../features/matters/pages/tabs/PortalTab/PortalSettings"))

// Case Prep sub-pages (BK pre-filing form management, nested under Filing tab)
const CasePrepOverview = lazyRetry(() => import("../features/bankruptcy/case-prep/pages/OverviewPage"))
const CasePrepCreditCounseling = lazyRetry(() => import("../features/bankruptcy/case-prep/pages/CreditCounselingPage"))
const CasePrepFormEditor = lazyRetry(() => import("../features/bankruptcy/case-prep/pages/FormEditorPage"))
const CasePrepScheduleC = lazyRetry(() => import("../features/bankruptcy/case-prep/pages/ScheduleCPage"))
const CasePrepMeansTest = lazyRetry(() => import("../features/bankruptcy/case-prep/pages/MeansTestPage"))
const CasePrepPlanBuilder = lazyRetry(() => import("../features/bankruptcy/case-prep/pages/PlanBuilderPage"))
const CasePrepValidation = lazyRetry(() => import("../features/bankruptcy/case-prep/pages/ValidationPage"))
const CasePrepFilingPackage = lazyRetry(() => import("../features/bankruptcy/case-prep/pages/FilingPackagePage"))
const CasePrepReaffirmation = lazyRetry(() => import("../features/bankruptcy/case-prep/pages/ReaffirmationPage"))
const CasePrepStatementOfIntention = lazyRetry(() => import("../features/bankruptcy/case-prep/pages/StatementOfIntentionPage"))
const CasePrepApprovals = lazyRetry(() => import("../features/bankruptcy/case-prep/pages/ApprovalsPage"))

// Filing Tab and sub-tabs (BK post-filing case management)
const FilingTab = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab"))
const FilingCreditors = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/CreditorsTab"))
const FilingCreditReports = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/CreditReportsTab"))
const FilingGarnishments = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/GarnishmentsTab"))
const FilingClientRequests = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/ClientRequestsTab"))
const FilingCaseInfo = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/CaseInfoTab"))
const FilingDocketEntries = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/DocketEntriesTab"))
const FilingObjections = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/ObjectionsTab"))
const FilingClaims = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/ClaimsTab"))
const FilingOverview = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/OverviewTab"))
const FilingCaseStage = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/CaseStageTab/index"))
const FilingConfirmationChecklist = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/ConfirmationChecklistTab"))
const FilingNotes = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/FiledNotesTab"))
const FilingPostDocs = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/PostFilingDocsTab"))
const FilingTrusteeUploads = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/TrusteeUploadsTab"))
const FilingFollowUps = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/FollowUpsTab"))
const FilingDeadlines = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/DeadlinesTab"))
const FilingApprovals = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/AttorneyApprovalsTab"))
const FilingHearings = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/HearingsTab"))
const FilingHearingQC = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/HearingQCTab"))
const FilingStudentLoanReview = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/StudentLoanReviewTab"))
const FilingConvertCase = lazyRetry(() => import("../features/matters/pages/tabs/FilingTab/tabs/ConvertCaseTab"))

// Loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  )
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Test pages - no auth required */}
        <Route path="/test" element={<TestLayout />}>
          <Route path="dave-knowledge" element={<DaveKnowledgePage />} />
        </Route>

        {/* Editor routes - outside dashboard layout for full-page editing */}
        <Route path="/editor" element={
          <RequireAuth>
            <EditorLayout />
          </RequireAuth>
        }>
          <Route path="signing/:matterId/:templateId" element={<SigningEditor />} />
          <Route path="signing/:matterId/draft/:draftId" element={<SigningEditor />} />
          <Route path="contract-templates/new" element={<ContractTemplateEditor />} />
          <Route path="contract-templates/:id/edit" element={<ContractTemplateEditor />} />
          <Route path="contract-send/:matterId/:templateId" element={<ContractSendEditor />} />
        </Route>

        {/* Dashboard routes wrapped in layout */}
        <Route path="/" element={
          <RequireAuth>
            <DashboardLayout />
          </RequireAuth>
        }>
          <Route index element={<Navigate to="/pipeline" replace />} />
          <Route path="overview" element={<Overview />} />
          <Route path="calendar" element={<CalendarPage />} />

          {/* Matters - filter routes (static paths take priority over dynamic) */}
          <Route path="matters" element={<Matters />} />
          <Route path="matters/mine" element={<Matters />} />
          <Route path="matters/new" element={<Matters />} />
          <Route path="matters/bookmarked" element={<Matters />} />
          <Route path="matters/overdue" element={<Matters />} />

          {/* Matter Detail - nested routes for deep linking */}
          <Route path="matters/:matterId" element={<MatterDetail />}>
            {/* Default to Overview */}
            <Route index element={<OverviewTab />} />
            <Route path="feed" element={<FeedTab />} />
            <Route path="events" element={<EventsTab />} />
            <Route path="billing" element={<BillingTab />}>
              <Route index element={<Navigate to="payments" replace />} />
              <Route path="payments" element={<BillingPaymentsTab />} />
              <Route path="methods" element={<BillingMethodsTab />} />
              <Route path="charge" element={<BillingChargeTab />} />
              <Route path="recurring" element={<BillingRecurringTab />} />
              <Route path="invoices" element={<BillingInvoicesTab />} />
              <Route path="trust" element={<BillingTrustTab />} />
              <Route path="schedule" element={<BillingScheduleTab />} />
              <Route path="events" element={<BillingEventsTab />} />
              <Route path="time" element={<BillingTimeTab />} />
            </Route>
            <Route path="settings" element={<SettingsTab />} />
            <Route path="custom-fields" element={<CustomFieldsTab />} />
            <Route path="custom-dates" element={<CustomDatesTab />} />
            <Route path="automations" element={<AutomationsTab />} />
            <Route path="approvals" element={<ApprovalsTab />} />
            <Route path="requests" element={<RequestsTab />} />
            <Route path="identifier" element={<IdentifiersTab />} />
            <Route path="lead-sources" element={<LeadSourcesTab />} />
            <Route path="analytics" element={<CommunicationAnalyticsTab />} />
            <Route path="note" element={<Navigate to="../feed" replace />} />
            <Route path="intake" element={<IntakeTab />} />
            <Route path="claimables" element={<ClaimablesTab />} />
            <Route path="parties-roles" element={<PartiesRolesTab />} />
            <Route path="notifications" element={<NotificationsTab />} />

            {/* Documents - nested routes */}
            <Route path="documents" element={<DocumentsTab />}>
              <Route index element={<Navigate to="categories" replace />} />
              <Route path="categories" element={<DocumentsCategoriesView />} />
              <Route path="list" element={<DocumentsListView />} />
              <Route path="pinned" element={<DocumentsPinnedView />} />
              <Route path="signing" element={<DocumentsSigningView />} />
              <Route path="templates" element={<DocumentsTemplatesView />} />
            </Route>

            {/* Items - nested routes */}
            <Route path="items" element={<ItemsTab />}>
              <Route index element={<Navigate to="outstanding" replace />} />
              <Route path="outstanding" element={<ItemsOutstanding />} />
              <Route path="completed" element={<ItemsCompleted />} />
            </Route>

            {/* Tasks - nested routes (in secondary nav) */}
            <Route path="tasks" element={<TasksTab />}>
              <Route index element={<Navigate to="step" replace />} />
              <Route path="step" element={<TasksStepTasks />} />
              <Route path="mine" element={<TasksMyTasks />} />
              <Route path="all" element={<TasksAllTasks />} />
              <Route path="finished" element={<TasksFinishedTasks />} />
            </Route>

            {/* Step management - nested routes */}
            <Route path="step" element={<StepTab />}>
              <Route index element={<Navigate to="move" replace />} />
              <Route path="move" element={<StepMoveStep />} />
              <Route path="jump" element={<StepJumpStep />} />
              <Route path="history" element={<StepHistory />} />
            </Route>

            {/* Forms - nested routes */}
            <Route path="forms" element={<FormsTab />}>
              <Route index element={<Navigate to="step" replace />} />
              <Route path="step" element={<FormsStepForms />} />
              <Route path="all" element={<FormsAllForms />} />
              <Route path="submissions" element={<FormsSubmissions />} />
            </Route>

            {/* Portal - nested routes (bankruptcy module) */}
            <Route path="portal" element={<PortalTab />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<PortalOverview />} />
              <Route path="questionnaire" element={<PortalQuestionnaire />} />
              <Route path="questionnaire/:section" element={<PortalQuestionnaire />} />
              <Route path="questionnaire/:section/:edit" element={<PortalQuestionnaire />} />
              <Route path="onboarding" element={<PortalOnboarding />} />
              <Route path="ccc" element={<PortalCCC />} />
              <Route path="creditors" element={<PortalCreditors />} />
              <Route path="import" element={<PortalImport />} />
              <Route path="docs" element={<PortalDocs />} />
              <Route path="items" element={<PortalItems />} />
              <Route path="docs-library" element={<PortalDocsLibrary />} />
              <Route path="settings" element={<PortalSettings />} />
            </Route>

            {/* Filing - nested routes (bankruptcy case management) */}
            <Route path="filing" element={<FilingTab />}>
              <Route index element={<Navigate to="creditors" replace />} />
              {/* Pre-Filing */}
              <Route path="creditors" element={<FilingCreditors />} />
              <Route path="credit-reports" element={<FilingCreditReports />} />
              <Route path="garnishments" element={<FilingGarnishments />} />
              <Route path="client-requests" element={<FilingClientRequests />} />
              {/* Pacer Notices */}
              <Route path="case-info" element={<FilingCaseInfo />} />
              <Route path="docket-entries" element={<FilingDocketEntries />} />
              <Route path="objections" element={<FilingObjections />} />
              <Route path="claims" element={<FilingClaims />} />
              {/* Post Filing */}
              <Route path="overview" element={<FilingOverview />} />
              <Route path="stage" element={<FilingCaseStage />} />
              <Route path="stage/:stageId" element={<FilingCaseStage />} />
              <Route path="confirmation-checklist" element={<FilingConfirmationChecklist />} />
              <Route path="notes" element={<FilingNotes />} />
              <Route path="post-docs" element={<FilingPostDocs />} />
              <Route path="trustee-uploads" element={<FilingTrusteeUploads />} />
              {/* Important Dates */}
              <Route path="follow-ups" element={<FilingFollowUps />} />
              <Route path="deadlines" element={<FilingDeadlines />} />
              <Route path="approvals" element={<FilingApprovals />} />
              <Route path="hearings" element={<FilingHearings />} />
              <Route path="hearing-qc" element={<FilingHearingQC />} />
              <Route path="student-loan-review" element={<FilingStudentLoanReview />} />
              <Route path="convert-case" element={<FilingConvertCase />} />
              {/* Case Preparation (pre-filing form management) */}
              <Route path="prep/overview" element={<CasePrepOverview />} />
              <Route path="prep/common-info" element={<Navigate to="../prep/overview" replace />} />
              <Route path="prep/credit-counseling" element={<CasePrepCreditCounseling />} />
              <Route path="prep/forms/:formType" element={<CasePrepFormEditor />} />
              <Route path="prep/schedule-c" element={<CasePrepScheduleC />} />
              <Route path="prep/means-test" element={<CasePrepMeansTest />} />
              <Route path="prep/plan" element={<CasePrepPlanBuilder />} />
              <Route path="prep/validation" element={<CasePrepValidation />} />
              <Route path="prep/package" element={<CasePrepFilingPackage />} />
              <Route path="prep/reaffirmation" element={<CasePrepReaffirmation />} />
              <Route path="prep/statement-of-intention" element={<CasePrepStatementOfIntention />} />
              <Route path="prep/approvals" element={<CasePrepApprovals />} />
            </Route>
          </Route>

          {/* Pipeline */}
          <Route path="pipeline" element={<Pipeline />} />

          {/* Contacts */}
          <Route path="contacts" element={<Contacts />} />
          <Route path="contacts/new" element={<Contacts />} />
          <Route path="contacts/:contactId" element={<ContactDetail />} />

          {/* Tasks */}
          <Route path="tasks" element={<Tasks />} />
          <Route path="tasks/today" element={<Tasks />} />
          <Route path="tasks/overdue" element={<Tasks />} />

          {/* Outstanding Items */}
          <Route path="items" element={<ItemsList />} />
          <Route path="items/overdue" element={<ItemsList />} />
          <Route path="items/today" element={<ItemsList />} />

          {/* Claimables */}
          <Route path="claimables" element={<ClaimablesList />} />
          <Route path="claimables/all" element={<ClaimablesList />} />

          {/* Calls */}
          <Route path="calls" element={<CallsList />} />
          <Route path="calls/:callId" element={<CallDetail />} />

          {/* Queue Metrics */}
          <Route path="queue-metrics" element={<QueueMetrics />} />

          {/* Documents (global - assigned to me) */}
          <Route path="documents" element={<DocumentsList />} />

          {/* Texts */}
          <Route path="texts" element={<TextsPage />} />
          <Route path="texts/new" element={<TextsPage />} />
          <Route path="texts/:threadId" element={<TextsPage />} />

          {/* Leads */}
          <Route path="leads" element={<LeadsList />} />
          <Route path="leads/mine" element={<LeadsList />} />
          <Route path="leads/live" element={<LeadsLive />} />
          <Route path="leads/:leadId" element={<LeadDetail />} />

          {/* Approvals */}
          <Route path="approvals" element={<ApprovalsList />} />
          <Route path="approvals/pending" element={<ApprovalsList />} />
          <Route path="approvals/returned" element={<ApprovalsList />} />
          <Route path="approvals/denied" element={<ApprovalsList />} />
          <Route path="approvals/mine" element={<ApprovalsList />} />

          {/* Requests */}
          <Route path="requests" element={<RequestsList />} />
          <Route path="requests/all" element={<RequestsList />} />
          <Route path="requests/pending" element={<RequestsList />} />
          <Route path="requests/approved" element={<RequestsList />} />
          <Route path="requests/denied" element={<RequestsList />} />

          {/* My Tickets */}
          <Route path="tickets" element={<MyTicketsList />} />
          <Route path="tickets/open" element={<MyTicketsList />} />
          <Route path="tickets/in_progress" element={<MyTicketsList />} />
          <Route path="tickets/resolved" element={<MyTicketsList />} />
          <Route path="tickets/closed" element={<MyTicketsList />} />

          {/* General Items */}
          <Route path="general-items" element={<GeneralItemsList />} />

          {/* Voicemail */}
          <Route path="voicemail" element={<VoicemailList />} />
          <Route path="voicemail/unresolved" element={<VoicemailList />} />
          <Route path="voicemail/resolved" element={<VoicemailList />} />
          <Route path="voicemail/all" element={<VoicemailList />} />
          <Route path="voicemail/:voicemailId" element={<VoicemailDetail />} />

          {/* Time Entries */}
          <Route path="time-entries" element={<TimeEntriesPage />} />
          <Route path="time-entries/all" element={<TimeEntriesPage />} />

          {/* Collections */}
          <Route path="collections" element={<CollectionsList />} />
          <Route path="collections/:filter" element={<CollectionsList />} />

          {/* Events */}
          <Route path="events" element={<EventsList />} />
          <Route path="events/today" element={<EventsList />} />
          <Route path="events/upcoming" element={<EventsList />} />
          <Route path="events/missed" element={<EventsList />} />

          {/* Mentions */}
          <Route path="mentions" element={<MentionsList />} />

          {/* Notifications */}
          <Route path="notifications/*" element={<NotificationsPage />} />

          {/* Changelog */}
          <Route path="changelog" element={<ChangelogPage />} />

          {/* Custom Views */}
          <Route path="custom-views" element={<CustomViewsList />} />
          <Route path="custom-views/:viewId" element={<CustomViewViewer />} />

          {/* Settings */}
          <Route path="settings" element={<SettingsPage />} />

          {/* Directory */}
          <Route path="directory" element={<DirectoryPage />} />

          {/* Signing */}
          <Route path="signing">
            <Route path="access" element={<SigningAccess />} />
            <Route path="templates" element={<SigningTemplatesList />} />
            <Route path="documents" element={<SigningDocumentsList />} />
            <Route path="documents/:documentId" element={<SigningDocumentView />} />
          </Route>

          {/* Training */}
          <Route path="training" element={<TrainingDashboard />} />
          <Route path="training/course/:courseId" element={<CourseViewer />} />
          <Route path="training/quiz/:quizId" element={<StandaloneQuiz />} />
          <Route path="training/scenarios" element={<ScenariosListPage />} />
          <Route path="training/simulation/:simulationId/review" element={<SimulationReviewPage />} />
          <Route path="training/simulation/:simulationId" element={<SimulationPlayer />} />

          {/* BK Modules - Standalone Pages */}
          <Route path="modules">
            <Route path="bk">
              <Route path="new-case-entries" element={<NewCaseEntriesPage />} />
              <Route path="dismissed-cases" element={<DismissedCasesPage />} />
              <Route path="converted-cases" element={<ConvertedCasesPage />} />
              <Route path="items" element={<BkItemsPage />} />
              <Route path="trustee-documents" element={<TrusteeDocumentsPage />} />
              <Route path="hearings" element={<BkHearingsPage />} />
            </Route>
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
